import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IoArrowBackOutline, IoCalendarOutline, IoTimeOutline } from "react-icons/io5";
import SubpageBanner from '../components/SubpageBanner';
import api from '../services/api';
import './NewsDetail.css';

export default function NewsDetail() {
    const { t } = useTranslation();
    const { slug, id } = useParams();
    const navigate = useNavigate();
    const [newsData, setNewsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Format date function
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Format time function
    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('tr-TR', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get base URL for images
    const getImageBaseUrl = () => {
        const origin = window.location.origin;
        const isLocalOrTest = origin.toLowerCase().includes('local') || origin.toLowerCase().includes('test');
        return isLocalOrTest ? origin : origin;
    };

    // Fetch news detail
    useEffect(() => {
        const fetchNewsDetail = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await api.getNewsById(id);
                if (response.success && response.data) {
                    setNewsData(response.data);
                } else {
                    setError('Haber yüklenirken bir hata oluştu.');
                }
            } catch (err) {
                console.error('Error fetching news detail:', err);
                setError('Haber yüklenirken bir hata oluştu.');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchNewsDetail();
        }
    }, [id]);

    if (loading) {
        return (
            <>
                <SubpageBanner
                    title={t('news.detail')}
                    page={t('news.page')}
                    path="/news"
                />
                <div className='news-detail-content'>
                    <div className="container">
                        <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                            <div className="text-center">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">{t('news.loading')}</span>
                                </div>
                                <p className="mt-3">{t('news.loadingNews')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <SubpageBanner
                    title={t('news.detail')}
                    page={t('news.page')}
                    path="/news"
                />
                <div className='news-detail-content'>
                    <div className="container">
                        <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                            <div className="text-center">
                                <div className="alert alert-danger">
                                    <h4>{t('news.error')}</h4>
                                    <p>{error}</p>
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => navigate('/news')}
                                    >
                                        {t('news.backToNews')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (!newsData) {
        return (
            <>
                <SubpageBanner
                    title={t('news.detail')}
                    page={t('news.page')}
                    path="/news"
                />
                <div className='news-detail-content'>
                    <div className="container">
                        <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                            <div className="text-center">
                                <div className="alert alert-warning">
                                    <h4>{t('news.error')}</h4>
                                    <p>{t('news.fetchError')}</p>
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => navigate('/news')}
                                    >
                                        {t('news.backToNews')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <SubpageBanner
                title={newsData.title}
                page={t('news.news')}
                path="/news"
            />
            <div className='news-detail-content'>
                <div className="container">
                    <button
                        className="btn-back"
                        onClick={() => navigate('/news')}
                    >
                        <IoArrowBackOutline size={18} />
                        <span>{t('news.backToNews')}</span>
                    </button>

                    <div className="news-detail-body">
                        {newsData.imageUrl && (
                            <div className="news-detail-image">
                                <img
                                    src={`${getImageBaseUrl()}/api/cms/images${newsData.imageUrl}`}
                                    alt={newsData.title}
                                />
                            </div>
                        )}

                        <div className="news-detail-meta">
                            <IoCalendarOutline size={18} />
                            <span>{formatDate(newsData.publishedAt)}</span>
                            <span className="meta-divider">•</span>
                            <IoTimeOutline size={18} />
                            <span>{formatTime(newsData.publishedAt)}</span>
                        </div>

                        {newsData.summary && (
                            <div className="news-detail-summary">
                                {newsData.summary}
                            </div>
                        )}

                        <div className="news-detail-text"
                            dangerouslySetInnerHTML={{
                                __html: newsData.content.replace(/\r\n/g, '<br>').replace(/\n/g, '<br>')
                            }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}
